/**
 * @file SetExpiryDialog.tsx
 * @description «Указать срок» у строки проведённой приёмки (phase19.22, W20;
 * ui-design §7): только «пусто → значение», с записью в журнал; этикетки
 * партии после этого устаревают и требуют перепечатки (W17-9c).
 *
 * Необратимость сказана до кнопки: исправить указанный срок нельзя, поэтому
 * дата сверяется с упаковкой здесь, а не после.
 *
 * phase19.31: поле — общее поле даты кабинета `DateField` (маска и
 * календарь) вместо нативного; кнопки — стандартного размера темы.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import { DateField } from "../ui/DateField";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

interface Props {
  /** Подпись строки: «Гидролат роза — 50 мл · партия RCP-…-5». */
  title: string;
  onSubmit: (expiresAt: string) => Promise<boolean>;
  onClose: () => void;
}

export function SetExpiryDialog({ title, onSubmit, onClose }: Props) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!value || saving) return;
    setSaving(true);
    const ok = await onSubmit(value);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Указать срок годности</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        {/* Сервер у проведённой строки принимает только срок (W20) — одно поле
            шириной под дату; подпись — как у остальных полей формы (SC43). */}
        <div className="flex flex-col gap-1">
          <label htmlFor="set-expiry-date" className="label-editorial">
            Годен до
          </label>
          <DateField id="set-expiry-date" value={value} onChange={setValue} title="Годен до" />
        </div>
        <p className="rounded-lg bg-warning/12 p-3 text-sm">
          Срок вписывается один раз — исправить его потом нельзя. Этикетки этой
          партии станут устаревшими: перепечатайте и переклейте их.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void submit()} disabled={!value || saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            <span className={saving ? "ml-2" : ""}>Указать срок</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
